import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { CVAPI } from '../../const';
import { CVSeccionMap, fixStructure, normalizeStructure } from '../../utils';

//
// ─── THUNKS ─────────────────────────────────────────────────────────────────────
//
export const createNewCv = createAsyncThunk('global/cv/createCv', async (args, thunkAPI) => {
  const req = await axios.post(`${CVAPI}/curriculum`, {
    validado: false,
    idUsuario: thunkAPI.getState().common.user.id,
  });
  return req.data;
});

export const fetchExtraData = createAsyncThunk('global/cv/extraData', async (args, thunkAPI) => {
  const resp = await axios.get(`${CVAPI}/curriculum/extraData`);
  return resp.data;
});

export const fetchInitialData = createAsyncThunk(
  'global/cv/fetchInitialData',
  async (args, thunkAPI) => {
    const id = args.id ? args.id : thunkAPI.getState().common.user.id;

    const currentCv = thunkAPI.getState().cv.data;
    //Ahorro la "llamada async", ya tengo el cv inicializado
    if (Object.keys(currentCv).length > 0 && currentCv.idUsuario === id)
      return {
        data: currentCv,
        format: false,
      };

    const response = await axios.get(`${CVAPI}/curriculum?usuario=${id}`).catch((error) => {
      throw error;
    });

    const data = response.data[0]; //Viene en arreglo
    if (!data)
      return thunkAPI.rejectWithValue({ code: 'noCV', msg: 'No se encuentra un CV, generando...' });

    return { data, format: true };
  },
);

export const validarItem = createAsyncThunk('globalsemi/cv/item/validar', async (args) => {
  const seccion = CVSeccionMap[args.seccionKey].api;
  await axios.patch(`${CVAPI}/${seccion}/${args.itemId}/validar`).catch((error) => {
    throw error;
  });

  return {
    seccionKey: args.seccionKey,
    itemId: args.itemId,
  };
});

export const newItem = createAsyncThunk('globalsemi/cv/item/new', async (args, thunkAPI) => {
  const seccion = CVSeccionMap[args.seccionKey].api;
  const data = {
    ...args.data,
    idCurriculumVitae: thunkAPI.getState().cv.data.id_cv,
  };
  const req = await axios.post(`${CVAPI}/${seccion}`, data).catch((error) => {
    throw error;
  });
  return {
    seccionKey: args.seccionKey,
    data: req.data,
  };
});

//Asume string con la clave de la seccion y id de item
export const deleteItem = createAsyncThunk('globalsemi/cv/item/delete', async (args, thunkAPI) => {
  const seccion = CVSeccionMap[args.seccionKey].api;
  await axios.delete(`${CVAPI}/${seccion}/${args.itemId}`).catch((error) => {
    throw error;
  });

  return {
    seccionKey: args.seccionKey,
    itemId: args.itemId,
  };
});

export const editItem = createAsyncThunk('globalsemi/cv/item/edit', async (args, thunkAPI) => {
  const seccion = CVSeccionMap[args.seccionKey];
  const cvState = thunkAPI.getState().cv;
  const item = cvState.data[args.seccionKey].find((el) => el.id === args.itemId);

  const req = await axios
    .put(`${CVAPI}/${seccion.api}/${args.itemId}`, { ...item, ...args.data })
    .catch((error) => {
      throw error;
    });

  return {
    seccionKey: args.seccionKey,
    itemId: args.itemId,
    data: req.data,
  };
});

export const cargarDoc = createAsyncThunk('globalsemi/cv/item/doc', async (args, thunkAPI) => {
  const seccion = CVSeccionMap[args.seccionKey].cv;
  let formData = new FormData();
  formData.append('file', args.file);
  formData.append('idItem', args.itemId);
  const req = await axios
    .post(`${CVAPI}/${seccion}/uploadFile`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .catch((error) => {
      throw error;
    });

  return {
    fileName: req.data.fileName,
    itemId: args.itemId,
    seccionKey: args.seccionKey,
  };
});

export const cargarAltDoc = createAsyncThunk(
  'globalsemi/cv/item/altDoc',
  async (args, thunkAPI) => {
    let formData = new FormData();
    formData.append('file', args.file);
    formData.append('idItem', args.itemId);
    const req = await axios
      .post(`${CVAPI}/uploadFile2`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .catch((error) => {
        throw error;
      });

    return {
      fileName: req.data.fileName,
      itemId: args.itemId,
      seccionKey: args.seccionKey,
    };
  },
);

export const verDoc = createAsyncThunk('globalsemi/cv/item/verDoc', async (args, thunkAPI) => {
  const req = await axios.get(`${CVAPI}/downloadFile/${args.docPath}`, {
    responseType: 'blob',
  });
  window.open(URL.createObjectURL(req.data));
  return true;
});

//
// ─── SLICE ──────────────────────────────────────────────────────────────────────
//
export const cvSlice = createSlice({
  name: 'cv',
  initialState: {
    data: {},
    extraData: {},
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExtraData.fulfilled, (state, action) => {
        const data = action.payload;

        state.extraData = {
          nivelesIdiomas: normalizeStructure(
            data.nivelIdiomaExtranjero,
            'id_nivel_idioma_extranjero',
          ),
          nivelInformatico: normalizeStructure(
            data.nivelConocimientoInformatico,
            'id_nivel_conocimiento_informatico',
          ),
          tiposAcademicos: normalizeStructure(
            data.tipoAntecedenteAcademico,
            'id_tipo_antecedente_academico',
          ),
          tiposLaborales: normalizeStructure(
            data.tipoAntecedenteLaboral,
            'id_tipo_antecedente_laboral',
          ),
          tiposPublicaciones: normalizeStructure(data.tipoPublicacion, 'id_tipo_publicacion'),
          tiposCursos: normalizeStructure(data.tipoCurso, 'id_tipo_curso'),
          tiposInformaticos: normalizeStructure(
            data.TipoConocimientoInformatico,
            'idTipoConocimientoInformatico',
          ),
          idiomas: normalizeStructure(data.Idiomas, 'id_idioma_nombre'),
          nivelEducativo: normalizeStructure(data.NivelEducativo, 'idNivelEducativoCv'),
        };
      })
      .addCase(fetchInitialData.fulfilled, (state, action) => {
        state.data = action.payload.format ? buildCvData(action.payload.data) : action.payload.data;
      })
      .addCase(createNewCv.fulfilled, (state, action) => {
        state.data = buildCvData(action.payload);
      })
      .addCase(deleteItem.fulfilled, (state, action) => {
        const { seccionKey, itemId } = action.payload;
        const id = state.data[seccionKey].findIndex((el) => el.id === itemId);
        state.data[seccionKey].splice(id, 1);
      })
      .addCase(editItem.fulfilled, (state, action) => {
        const payload = action.payload;
        const pk = CVSeccionMap[payload.seccionKey].pk;
        const idx = state.data[payload.seccionKey].findIndex((el) => el.id === payload.itemId);
        state.data[payload.seccionKey][idx] = {
          ...payload.data,
          id: payload.data[pk],
        };
      })
      .addCase(newItem.fulfilled, (state, action) => {
        const payload = action.payload;
        const pk = CVSeccionMap[payload.seccionKey].pk;
        state.data[payload.seccionKey].push({
          ...payload.data,
          id: payload.data[pk],
        });
      })
      .addCase(validarItem.fulfilled, (state, action) => {
        const payload = action.payload;
        const idx = state.data[payload.seccionKey].findIndex((el) => el.id === payload.itemId);
        state.data[payload.seccionKey][idx].validado = true;
      })
      .addCase(cargarDoc.fulfilled, (state, action) => {
        const payload = action.payload;
        const idx = state.data[payload.seccionKey].findIndex((el) => el.id === payload.itemId);
        state.data[payload.seccionKey][idx].documentoPath = payload.fileName;
        // TEST ADDING STATE DATA - ALAN
        // state.data[payload.seccionKey][idx].documentDate = payload.documentDate;
      })
      .addCase(cargarAltDoc.fulfilled, (state, action) => {
        const payload = action.payload;
        const idx = state.data[payload.seccionKey].findIndex((el) => el.id === payload.itemId);
        state.data[payload.seccionKey][idx].documentoPath2 = payload.fileName;
      });
  },
});

const buildCvData = (data) => {
  const cv = {
    id_cv: data.idCurriculumVitae,
    idUsuario: data.idUsuario,
    antecedentesAcademicos: fixStructure(
      data[CVSeccionMap.antecedentesAcademicos.cv],
      CVSeccionMap.antecedentesAcademicos.pk,
    ),
    antecedentesLaborales: fixStructure(
      data[CVSeccionMap.antecedentesLaborales.cv],
      CVSeccionMap.antecedentesLaborales.pk,
    ),
    institucionales: fixStructure(
      data[CVSeccionMap.institucionales.cv],
      CVSeccionMap.institucionales.pk,
    ),
    capacitacion: fixStructure(data[CVSeccionMap.capacitacion.cv], CVSeccionMap.capacitacion.pk),
    publicaciones: fixStructure(data[CVSeccionMap.publicaciones.cv], CVSeccionMap.publicaciones.pk),
    idiomas: fixStructure(data[CVSeccionMap.idiomas.cv], CVSeccionMap.idiomas.pk),
    informaticos: fixStructure(data[CVSeccionMap.informaticos.cv], CVSeccionMap.informaticos.pk),
  };
  return cv;
};

export const { loadInitial } = cvSlice.actions;

export const cvSelector = (state) => state.cv.data;
export const cvTiposSelector = (state) => state.cv.extraData;

export default cvSlice.reducer;
